import React, { useState, useEffect } from 'react';
import { createClient } from "@supabase/supabase-js";
import supabase from '../../utils/supabaseClient';
import imageCompression from 'browser-image-compression';

const translations = {
  es: {
    title: "Agregar Subfamilia",
    name: "Nombre de la subfamilia",
    familiaId: "Familia",
    thumbnail: "URL de la imagen (opcional)",
    add: "Agregar",
    success: "¡Subfamilia agregada exitosamente!",
    error: "Error al agregar la subfamilia.",
    required: "Este campo es obligatorio",
    upload: "Subir nueva imagen"
  },
  en: {
    title: "Add Subfamily",
    name: "Subfamily Name",
    familiaId: "Family",
    thumbnail: "Image URL (optional)",
    add: "Add",
    success: "Subfamily added successfully!",
    error: "Error adding subfamily.",
    required: "This field is required",
    upload: "Upload new image"
  }
};



function AddSubfamilia({ lang = 'es', onClose, onAdded }) {
  
  const t = translations[lang] || translations.es;
  const [form, setForm] = useState({ nombre: '', familia_id: '', thumbnail: '' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [familias, setFamilias] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(null);
  const [thumbnails, setThumbnails] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const imagesPerPage = 10;

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => setUser(user));
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => {
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    async function fetchFamilias() {
      try {
        const { data } = await supabase.from('familia').select('id, nombre');
        if (data) {
          setFamilias(data);
        }
      } catch (err) {
        console.error('Exception in fetchFamilias:', err);
      }
    }
    fetchFamilias();
  }, []);

  // Fetch thumbnails from bucket
  useEffect(() => {
    async function fetchAllThumbnails(path = 'thumbnails') {
      let allFiles = [];
      const { data, error } = await supabase
        .storage
        .from('productsamples')
        .list(path, { limit: 100, offset: 0 });
      if (error) {
        console.error('Error fetching thumbnails:', error);
        return [];
      }
      for (const item of data) {
        if (item.metadata) {
          allFiles.push({ name: item.name, path: `${path}/${item.name}` });
        } else {
          const subFiles = await fetchAllThumbnails(`${path}/${item.name}`);
          allFiles = allFiles.concat(subFiles);
        }
      }
      return allFiles;
    }

    fetchAllThumbnails().then(files => {
      setThumbnails(
        files
          .filter(f => f.name.match(/\.(jpg|jpeg|png|webp|png)$/i))
          .map(f => ({
            ...f,
            path: supabase.storage.from('productsamples').getPublicUrl(f.path).data.publicUrl
          }))
      );
    });
  }, []);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setMessage('');
    if (!form.nombre || !form.familia_id) {
      setMessage(t.required);
      return;
    }
    setLoading(true);
    const { error } = await supabase.from('subfamilia').insert([{
      nombre: form.nombre,
      familia_id: form.familia_id,
      thumbnail: form.thumbnail || null
    }]);
    setLoading(false);
    if (error) {
      setMessage(t.error);
    } else {
      setMessage(t.success);
      setForm({ nombre: '', familia_id: '', thumbnail: '' });
      if (onAdded) onAdded(); // Notify parent to refresh list if needed
      if (onClose) setTimeout(onClose, 800); // Close popup after short delay
    }
  };

  // Add this handler for file upload
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);

    // Resize if needed
    let uploadFile = file;
    if (file.size > 0) {
      uploadFile = await imageCompression(file, {
        maxWidthOrHeight: 512,
        useWebWorker: true
      });
    }

    const filePath = `thumbnails/${Date.now()}_${uploadFile.name}`;
    const { error } = await supabase.storage
      .from('productsamples')
      .upload(filePath, uploadFile, { upsert: false });

    setUploading(false);

    if (!error) {
      // Get the public URL
      const { data } = supabase.storage.from('productsamples').getPublicUrl(filePath);
      setForm({ ...form, thumbnail: data.publicUrl });
      setThumbnails([...thumbnails, { name: filePath.split('/').pop(), path: data.publicUrl }]);
    } else {
      alert('Error uploading image');
    }
  };

  // Pagination logic
  const totalPages = Math.ceil(thumbnails.length / imagesPerPage);
  const startIdx = (currentPage - 1) * imagesPerPage;
  const endIdx = startIdx + imagesPerPage;
  const thumbnailsToShow = thumbnails.slice(startIdx, endIdx);

  return (
    <div style={{ maxWidth: 400, margin: '40px auto', padding: 24, borderRadius: 8, background: '#f7f7f7' }}>
      <h2 style={{ textAlign: 'center', marginBottom: 24 }}>{t.title}</h2>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label>
          {t.name}
          <input
            name="nombre"
            value={form.nombre}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }}
          />
        </label>
        <label>
          {t.familiaId}
          <select
            name="familia_id"
            value={form.familia_id}
            onChange={handleChange}
            required
            style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid #ccc', marginTop: 4 }}
          >
            <option value="">{lang === 'es' ? 'Seleccione una familia' : 'Select a family'}</option>
            {familias.map(f => (
              <option key={f.id} value={f.id}>
                {f.nombre}
              </option>
            ))}
          </select>
        </label>
        <label>
          {t.thumbnail}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, margin: '10px 0' }}>
            {thumbnailsToShow.map(img => (
              <img
                key={img.path}
                src={img.path}
                alt={img.name}
                style={{
                  width: 60,
                  height: 60,
                  objectFit: 'cover',
                  borderRadius: 4,
                  border: form.thumbnail === img.path ? '3px solid #e60026' : '2px solid #ccc',
                  cursor: 'pointer',
                  boxSizing: 'border-box'
                }}
                onClick={() => setForm({ ...form, thumbnail: img.path })}
                title={img.name}
              />
            ))}
          </div>
          {/* Pagination controls */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
            >
              &lt;
            </button>
            <span style={{ alignSelf: 'center' }}>
              {currentPage} / {totalPages || 1}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              style={{ padding: '2px 8px', borderRadius: 4, border: '1px solid #ccc', background: '#fff', cursor: currentPage === totalPages || totalPages === 0 ? 'not-allowed' : 'pointer' }}
            >
              &gt;
            </button>
          </div>
          <div style={{ margin: '10px 0', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <label htmlFor="thumbnail-upload" style={{ marginBottom: 6, fontWeight: 500, textAlign: 'center' }}>
              {t.upload}
            </label>
            <label
              htmlFor="thumbnail-upload"
              style={{
                display: 'inline-block',
                padding: '8px 18px',
                background: '#e60026',
                color: '#fff',
                borderRadius: 6,
                cursor: uploading ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                marginBottom: 8,
                boxShadow: '0 2px 8px rgba(0,0,0,0.07)'
              }}
            >
              {uploading ? (lang === 'es' ? 'Subiendo...' : 'Uploading...') : (lang === 'es' ? 'Elegir archivo' : 'Choose file')}
              <input
                id="thumbnail-upload"
                type="file"
                accept="image/*"
                onChange={handleThumbnailUpload}
                disabled={uploading}
                style={{ display: 'none' }}
              />
            </label>
          </div>
          {form.thumbnail && (
            <img
              src={form.thumbnail}
              alt="thumbnail"
              style={{ width: 160, height: 160, marginTop: 8, borderRadius: 8, objectFit: 'cover', boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }}
            />
          )}
        </label>
        {message && (
          <div style={{ color: message === t.success ? 'green' : '#e60026', fontSize: 15, textAlign: 'center' }}>
            {message}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="submit"
            disabled={loading}
            style={{
              background: '#e60026',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              padding: '10px 0',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              flex: 1
            }}
          >
            {t.add}
          </button>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              style={{
                background: '#fff',
                color: '#e60026',
                border: '1px solid #e60026',
                borderRadius: 6,
                padding: '10px 0',
                fontWeight: 600,
                cursor: 'pointer',
                flex: 1
              }}
            >
              {lang === 'es' ? 'Cancelar' : 'Cancel'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

export default AddSubfamilia;
